// /brokers — the broker directory (SM-716, SM-621 phase 08a).
//
// Every broker node the exchange operates — roots and their children —
// searchable by name or exact id, filtered by own status, narrowed to
// roots when the operator is looking for an organisation rather than a
// branch. Opening a row leads to the node's detail page, where its
// lifecycle, hierarchy, staff, accounts, funding, risk and audit trail
// live. Filters sit in the URL so a view is shareable.
//
// The list is broker-service's GET /admin/brokers (BROKER_VIEW): by
// default every status but CLOSED; the status filter picks one, or
// widens to closed nodes too. Its rows carry the node's OWN status —
// the effective one (an ancestor may restrict it further) is resolved on
// the detail page.
//
// The route is /brokers/…, deliberately NOT under /admin: the dev server
// proxies the /admin prefix to the API, and a console page there would
// be swallowed by it.
//
// Create broker (SM-963, BROKER_CREATE) opens the create dialog: a new
// organisation or a sub-broker under one the operator picks.
import { useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { BrokerStatusBadges } from "@/components/brokers/BrokerBadges";
import { CreateBrokerDialog } from "@/components/brokers/CreateBrokerDialog";
import { brokerName } from "@/components/brokers/governance";
import { msToIso } from "@/components/brokers/receivables";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchBrokers } from "@/lib/broker-queries";
import { BROKER_STATUSES, type BrokerItem } from "@/lib/broker-types";
import { fmtDate } from "@/lib/format";
import { Perm } from "@/lib/perms";

const PAGE_SIZE = 25;

/** ?status=ALL widens to every status, closed included. */
const ANY_STATUS = "ALL";

interface Search {
  q?: string;
  status?: string;
  roots?: "1";
  page?: number;
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

export const Route = createFileRoute("/_app/brokers/")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    q: str(s.q),
    status: s.status === ANY_STATUS ? ANY_STATUS : BROKER_STATUSES.find((st) => st === s.status),
    roots: s.roots === "1" || s.roots === 1 ? "1" : undefined,
    page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
  }),
  component: BrokerDirectoryPage,
});

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

const STATUS_OPTIONS = [
  { value: "", label: "Any status but closed" },
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAUSED", label: "Paused" },
  { value: "CLOSING", label: "Closing" },
  { value: "CLOSED", label: "Closed" },
  { value: ANY_STATUS, label: "Any status, closed included" },
];

type Scope = "all" | "roots";

function BrokerDirectoryPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canView = perms.has(Perm.BrokerView);
  const canCreate = perms.has(Perm.BrokerCreate);
  const [creating, setCreating] = useState(false);

  const page = search.page ?? 0;
  const filter = {
    q: search.q,
    status: search.status && search.status !== ANY_STATUS ? search.status : undefined,
    include_closed: search.status === ANY_STATUS,
    roots_only: search.roots === "1",
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const q = useQuery({
    queryKey: brokerKeys.list(filter),
    queryFn: () => fetchBrokers(filter),
    placeholderData: keepPreviousData,
    enabled: canView,
  });
  const rows = q.data?.items ?? [];

  function apply(next: Partial<Search>) {
    void navigate({ search: (s) => ({ ...s, ...next, page: undefined }) });
  }

  if (perms.loaded && !canView) {
    return (
      <EmptyState title="No access" description="You need the BROKER_VIEW permission to see the broker directory." />
    );
  }

  const narrowed = Boolean(search.q || search.status || search.roots);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight">Broker directory</h1>
          <p className="text-sm text-muted">
            Every broker organisation and sub-broker the exchange operates. Open one to review its lifecycle,
            hierarchy, staff, serviced accounts, funding, risk and audit trail.
          </p>
        </div>
        {canCreate ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            Create broker
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Input
            label="Search"
            placeholder="Name or exact broker id…"
            defaultValue={search.q ?? ""}
            onKeyDown={(e) => {
              if (e.key === "Enter") apply({ q: str((e.target as HTMLInputElement).value) });
            }}
            onBlur={(e) => apply({ q: str(e.target.value) })}
          />
        </div>
        <div className="w-64">
          <Select
            label="Status"
            value={search.status ?? ""}
            onChange={(e) => apply({ status: e.target.value || undefined })}
            options={STATUS_OPTIONS}
          />
        </div>
        <SegmentedControl<Scope>
          label="Scope"
          value={search.roots === "1" ? "roots" : "all"}
          onChange={(v) => apply({ roots: v === "roots" ? "1" : undefined })}
          options={[
            { value: "all", label: "All nodes" },
            { value: "roots", label: "Roots only" },
          ]}
        />
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Brokers</CardTitle>
          <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {(q.isLoading || !perms.loaded) && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {q.isError && <ErrorPanel error={q.error} reset={() => void q.refetch()} />}
          {q.data && rows.length === 0 && (
            <div className="space-y-3 px-5 py-16 text-center">
              <p className="text-sm text-muted">
                {narrowed ? "No broker matches the current filters." : "No broker has been created yet."}
              </p>
              {!narrowed && canCreate ? (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  Create broker
                </Button>
              ) : null}
            </div>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Broker</th>
                    <th className={th}>Status</th>
                    <th className={th}>Place in the tree</th>
                    <th className={th}>Created</th>
                    <th className={th}>Status changed</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <DirectoryRow
                      key={b.broker_id}
                      b={b}
                      onOpen={() => void navigate({ to: "/brokers/$id", params: { id: b.broker_id } })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {q.data && q.data.page.total > 0 && (
            <Pagination
              page={q.data.page}
              currentPage={page}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p > 0 ? p : undefined }) })}
              itemLabel="entries"
            />
          )}
        </CardBody>
      </Card>

      {canCreate ? <CreateBrokerDialog open={creating} onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function DirectoryRow({ b, onOpen }: { b: BrokerItem; onOpen: () => void }) {
  const isRoot = !b.parent_broker_id;
  return (
    <tr
      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
      tabIndex={0}
    >
      <td className={`${td} px-5`}>
        <Link
          to="/brokers/$id"
          params={{ id: b.broker_id }}
          className="font-medium text-t-1 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {brokerName(b)}
        </Link>
        {b.legal_name && b.legal_name !== b.display_name ? <div className="text-2xs text-muted">{b.legal_name}</div> : null}
        <div className="font-mono text-2xs text-muted">{b.broker_id}</div>
      </td>
      <td className={td}>
        <div className="flex flex-wrap items-center gap-1.5">
          <BrokerStatusBadges broker={b} />
          {b.is_system ? <Badge tone="accent">System</Badge> : null}
        </div>
      </td>
      <td className={td}>
        {isRoot ? (
          <Badge tone="accent">Root</Badge>
        ) : (
          <div className="space-y-0.5">
            <div className="text-2xs text-muted">Depth {b.broker_depth}</div>
            <div className="font-mono text-2xs text-muted">{b.root_broker_id}</div>
          </div>
        )}
      </td>
      <td className={`${td} text-2xs text-muted`}>{fmtDate(msToIso(b.created_at_unix_ms))}</td>
      <td className={`${td} text-2xs text-muted`}>{fmtDate(msToIso(b.status_changed_at_unix_ms))}</td>
    </tr>
  );
}
